import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  Container,
  Paper,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import { useAuth } from "../context/AuthContext";
import { useSnackbar } from "../context/SnackbarContext";
import { api } from "../services/api";
import {
  ApiTemplate,
  TEMPLATES_QUERY_KEY,
  useTemplates,
} from "../hooks/useTemplates";
import { Template } from "../components/TemplateCreationModal";
import TemplateCreationModal from "../components/TemplateCreationModal";

// Keeps the raw notice type (unlike Create notice) and the organizationId,
// which identifies system templates.
const mapApiTemplates = (templates: ApiTemplate[]): Template[] =>
  templates.map((t) => ({
    id: t.templateId,
    templateId: t.templateId,
    name: t.name,
    title: t.title,
    description: t.description,
    background: t.background,
    noticeType: t.noticeType as Template["noticeType"],
    isUrgent: t.isUrgent,
    audience: t.audience,
    targetSubcircle: t.targetSubcircle,
    createdAt: t.createdAt,
    usageCount: t.usageCount,
    organizationId: t.organizationId,
  }));

const CreateSystemTemplate: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  const { showMessage } = useSnackbar();
  const queryClient = useQueryClient();
  const { data: templates = [], isLoading: loading } = useTemplates({
    select: mapApiTemplates,
    enabled: process.env.NODE_ENV !== "production",
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null);
  const [isAuthorized, setIsAuthorized] = useState(false);

  // Check if in production - block access
  useEffect(() => {
    if (process.env.NODE_ENV === "production") {
      navigate("/", { replace: true });
      return;
    }

    if (!searchParams.has("system")) {
      navigate("/", { replace: true });
      return;
    }

    setIsAuthorized(true);
  }, [navigate, searchParams]);

  // Check authentication
  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
    }
  }, [token, navigate]);

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setIsModalOpen(true);
  };

  const handleEdit = (template: Template) => {
    setEditingTemplate(template);
    setIsModalOpen(true);
  };

  const handleSave = (_template: Template) => {
    queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
    setIsModalOpen(false);
    setEditingTemplate(null);
  };

  const handleDelete = async (templateId: string) => {
    if (!token) return;

    try {
      await api.templates.delete(templateId, token);
      queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEY });
    } catch (error) {
      console.error("Error deleting template:", error);
      showMessage({
        message: "Failed to remove template.",
        severity: "error",
      });
    }
  };

  // Show debug info first
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Paper sx={{ p: 4 }}>
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" sx={{ mb: 2, fontWeight: 600 }}>
            System Template Creator (Debug)
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              NODE_ENV: {process.env.NODE_ENV || "undefined"}
            </Typography>
            <Typography variant="body2">
              System Param: {searchParams.get("system") || "missing"}
            </Typography>
            <Typography variant="body2">
              Has Token: {token ? "Yes" : "No"}
            </Typography>
            <Typography variant="body2">
              Is Authorized: {isAuthorized ? "Yes" : "No"}
            </Typography>
            <Typography variant="body2">
              Search Params: {searchParams.toString() || "none"}
            </Typography>
          </Alert>
        </Box>

        {!isAuthorized || !token ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", minHeight: "200px", gap: 2 }}>
            <CircularProgress />
            <Typography>Checking access...</Typography>
          </Box>
        ) : (
          <>
            <Button
              variant="contained"
              onClick={handleCreateNew}
              sx={{ mb: 3 }}
            >
              Create New System Template
            </Button>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Existing Templates ({templates.length})
                </Typography>
                {templates.length === 0 ? (
                  <Typography color="text.secondary">
                    No templates yet. Create your first system template above.
                  </Typography>
                ) : (
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    {templates.map((template) => (
                      <Paper
                        key={template.id}
                        sx={{
                          p: 2,
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <Box>
                          <Typography variant="h6">{template.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {template.title} • {template.noticeType}
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleEdit(template)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => {
                              if (
                                window.confirm(
                                  "Are you sure you want to remove this template?"
                                )
                              ) {
                                handleDelete(template.id);
                              }
                            }}
                          >
                            Remove
                          </Button>
                        </Box>
                      </Paper>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </>
        )}
      </Paper>

      <TemplateCreationModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingTemplate(null);
        }}
        onSave={handleSave}
        templateToEdit={editingTemplate}
        disableAudienceSelection={true}
        isSystemTemplate={true}
      />
    </Container>
  );
};

export default CreateSystemTemplate;
